// src/router/index.js
import axios from "@/axiosInstance";
import { useToast } from "vue-toast-notification";
import { createRouter, createWebHistory } from "vue-router";
import store from "@/store";
import { listiniRedirectFor } from "@/config/listiniCategories";
import { setupRedirectFor } from "@/utilities/setupGate";
import { platformRedirectFor } from "@/utilities/platformGate";
import { primaPaginaVisibile } from "@/utilities/layoutPlus";

// --- Pagine (come nel tuo file originale) ---
import NotFound from "../components/notFound.vue";
import LoginPage from "../views/LoginPage.vue";
import SendEmailPage from "../views/SendEmailPage.vue";
import SetNewPasswordPage from "../views/SetNewPasswordPage.vue";

import AccountPage from "../views/AccountPage.vue";
import HomePage from "../views/HomePage.vue";

import ClientPage from "../views/ClientPage.vue";
import CreateClientPage from "../views/CreateClientPage.vue";
import EditClientPage from "../views/EditClientPage.vue";

import CreateItemPage from "../views/CreateItemPage.vue";
import EditItemPage from "../views/EditItemPage.vue";
import ListiniPage from "../views/ListiniPage.vue";
import ListiniCategoryPage from "../views/ListiniCategoryPage.vue";
import MagazzinoPage from "../views/MagazzinoPage.vue";

import CreateQuotePage from "../views/CreateQuotePage.vue";
import EditQuotePage from "../views/EditQuotePage.vue";
import QuotePage from "../views/QuotePage.vue";

import OrderPage from "../views/OrderPage.vue";
import OrdiniPage from "../views/OrdiniPage.vue";
import FatturazionePage from "../views/FatturazionePage.vue";
import FatturaDettaglioPage from "../views/FatturaDettaglioPage.vue";

// ---------------------------------------------------------------------------
// Endpoint BE per verificare il cookie JWT (deve rispondere 200 o 401/403)
// ---------------------------------------------------------------------------
const WHO_AM_I = "/auth/me";

// Micro-cache in memoria per lo stato auth
let fetchedOnce = false;
let cachedAuth = false;
let cachedUser = null;

export const setAuth = (val) => {
  cachedAuth = val;
  if (!val) {
    cachedUser = null;
    store.commit("CLEAR_CURRENT_USER");
  }
};
export const authIsCached = () => cachedAuth;

// ---------------------------------------------------------------------------
// Definizione rotte con meta requiresAuth / requiresGuest
// ---------------------------------------------------------------------------
const routes = [
  // Catch-all (mantengo il tuo NotFound, ma puoi anche redirigere a /login)
  {
    path: "/:pathMatch(.*)*",
    component: NotFound,
    meta: { requiresAuth: false, requiresGuest: false },
  },

  // Root → /login
  {
    path: "/",
    redirect: "/login",
  },

  // ---- Area pubblica (guest) ----
  {
    path: "/login",
    name: "login",
    component: LoginPage,
    meta: {
      breadcrumb: "Login",
      hideAccountLink: true,
      hideUrlIndex: true,
      showLogo: true,
      requiresGuest: true, // <--- solo utenti NON loggati
      platformBrand: true, // pagina di tutti i clienti: veste crmstart
    },
    children: [
      {
        path: "verify-email",
        name: "send-email",
        component: SendEmailPage,
        meta: {
          breadcrumb: "Recupera Password",
          hideAccountLink: true,
          hideLogout: true,
          hideUrlIndex: false,
          showLogo: true,
          requiresGuest: true,
        },
      },
      {
        path: "reset-password",
        name: "set-new-password",
        component: SetNewPasswordPage,
        meta: {
          breadcrumb: "Reimposta Password",
          hideAccountLink: true,
          hideLogout: true,
          hideUrlIndex: false,
          showLogo: true,
          requiresGuest: true,
        },
      },
      {
        path: "/",
        redirect: "/login",
      },
    ],
  },

  // ---- Registrazione di un cliente nuovo ----
  // [sc-48497 / task 48525] Fuori da /login e non figlia sua: chi arriva qui
  // un accesso non ce l'ha, e la pagina prende tutto lo spazio. `requiresGuest`
  // perché chi è già dentro un'azienda non ne registra un'altra da qui.
  {
    path: "/registrazione",
    name: "registrazione",
    component: () => import("../views/RegisterPage.vue"),
    meta: {
      breadcrumb: "Registrazione",
      hideAccountLink: true,
      hideUrlIndex: true,
      showLogo: true,
      requiresGuest: true,
      platformBrand: true,
    },
  },
  {
    path: "/registrazione/conferma",
    name: "registrazione-conferma",
    component: () => import("../views/VerifyRegistrationPage.vue"),
    meta: {
      breadcrumb: "Conferma registrazione",
      hideAccountLink: true,
      hideUrlIndex: true,
      showLogo: true,
      requiresGuest: true,
      platformBrand: true,
    },
  },

  // ---- Configurazione iniziale ----
  // [sc-48350 / task 48396] Rotta di primo livello e non figlia di /home: la
  // procedura prende tutta la pagina, perché finché non è finita non c'è
  // nessun gestionale in cui navigare. Niente `requiresAdmin`: ci arriva anche
  // chi amministratore non è, e ci trova la schermata d'attesa invece di una
  // home vuota senza spiegazioni.
  {
    path: "/configurazione",
    name: "configurazione",
    component: () => import("../views/SetupPage.vue"),
    meta: {
      breadcrumb: "Configurazione",
      hideAccountLink: true,
      hideUrlIndex: true,
      showLogo: true,
      requiresAuth: true,
    },
  },

  // ---- Console di piattaforma [sc-48497 / task 48534] ----
  //
  // Fuori da /home di proposito: non è una scheda del gestionale, è l'unica
  // applicazione che l'amministratore di crmstart vede. Il gestionale, per
  // lui, sarebbe una sequenza di 403 — dalla 48534 l'API non lo lascia più
  // entrare nei dati di un cliente. `platformOnly` è la marca che il cancello
  // legge, in tutt'e due i versi: chi è di piattaforma sta qui, chi è di
  // un'azienda no.
  {
    path: "/piattaforma",
    name: "piattaforma",
    component: () => import("../views/PlatformCompaniesPage.vue"),
    meta: {
      breadcrumb: "Piattaforma",
      hideAccountLink: true,
      hideUrlIndex: true,
      showLogo: true,
      requiresAuth: true,
      platformOnly: true,
    },
  },
  {
    path: "/piattaforma/registro",
    name: "piattaforma-registro",
    component: () => import("../views/PlatformViewsPage.vue"),
    meta: {
      breadcrumb: "Registro",
      hideAccountLink: true,
      hideUrlIndex: true,
      showLogo: true,
      requiresAuth: true,
      platformOnly: true,
    },
  },
  {
    path: "/piattaforma/azienda/:id",
    name: "piattaforma-azienda",
    component: () => import("../views/PlatformCompanyPage.vue"),
    meta: {
      breadcrumb: "Azienda",
      hideAccountLink: true,
      hideUrlIndex: true,
      showLogo: true,
      requiresAuth: true,
      platformOnly: true,
    },
  },

  // ---- Area privata (richiede login) ----
  {
    path: "/home",
    name: "home",
    component: HomePage,
    meta: {
      breadcrumb: "Home",
      hideAccountLink: false,
      hideUrlIndex: true,
      showLogo: true,
      requiresAuth: true, // <--- TUTTO /home è protetto
    },
    children: [
      {
        path: "account",
        name: "account",
        component: AccountPage,
        meta: {
          breadcrumb: "Account",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          // [sc-48351 / task 48786] Account lo vedono tutti: gli altri ruoli
          // ci trovano il cambio password e il riquadro Operatori.
        },
      },
      {
        // [sc-48350 / task 48396] Rimodifica del marchio: quello che il
        // cliente ha detto una volta sul proprio marchio deve poter essere
        // corretto senza aprire un terminale. Le risposte su come vende
        // restano invece fissate al primo accesso.
        path: "marchio",
        name: "marchio",
        component: () => import("../views/BrandPage.vue"),
        meta: {
          breadcrumb: "Configurazione azienda",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        // [sc-48351 / task 48782] Il log attività: chi ha fatto cosa. Sta fra
        // le impostazioni come «I tuoi dati», e come lei si guarda di rado.
        // Le righe le **scrivono** tutte le aziende; la pagina la vede solo
        // PLUS, e l'API rifiuta la lettura a START con FEATURE_NOT_IN_PLAN.
        path: "log",
        name: "log-attivita",
        component: () => import("../views/LogAttivitaPage.vue"),
        meta: {
          breadcrumb: "Log attività",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        // [sc-48351 / task 48786] I ruoli dell'azienda: solo l'amministratore,
        // solo PLUS (in START l'API risponde FEATURE_NOT_IN_PLAN, come per il
        // log). Nessun `permission`: non è un'area, è la gestione.
        path: "ruoli",
        name: "ruoli",
        component: () => import("../views/RuoliPage.vue"),
        meta: {
          breadcrumb: "Ruoli",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        // [sc-48497 / task 48575] Lo scarico dei propri dati. Sta fra le
        // impostazioni e non in home perche' si usa di rado, ma deve esistere
        // una pagina a cui mandare chi sta per perdere l'accesso in scrittura:
        // e' il presupposto della cancellazione a 60 giorni (task 48532).
        path: "dati",
        name: "esportazione",
        component: () => import("../views/ExportPage.vue"),
        meta: {
          breadcrumb: "I tuoi dati",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: "clienti",
        name: "customers",
        component: ClientPage,
        meta: {
          breadcrumb: "Clienti",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          permission: ["clienti", "V"],
          requiresAdmin: true,
        },
        children: [
          {
            path: "nuovo",
            name: "add-client",
            component: CreateClientPage,
            meta: {
              writes: true, // [sc-48497] chiusa a prova finita
              breadcrumb: "Nuovo cliente",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              permission: ["clienti", "C"],
              requiresAdmin: true,
            },
          },
          {
            path: "modifica/:clientId",
            name: "edit-client",
            component: EditClientPage,
            props: true,
            meta: {
              writes: true, // [sc-48497] chiusa a prova finita
              breadcrumb: "Modifica cliente",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              permission: ["clienti", "M"],
              requiresAdmin: true,
            },
          },
        ],
      },
      {
        path: "stock",
        redirect: "/home/listini",
      },
      {
        path: "listini",
        name: "listini",
        component: ListiniPage,
        meta: {
          breadcrumb: "Listini",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          permission: ["listini", "V"],
        },
        children: [
          {
            // [sc-48351 / task 48783] Variazioni di prezzo del listino, in
            // massa. Sta prima di `:category` e non è un caso: è un percorso
            // fisso, e vue-router lo preferisce comunque a quello dinamico —
            // ma leggerlo qui sopra ricorda che «variazioni» non può essere
            // lo slug di una scheda.
            //
            // `writes: true`: applicare e annullare sono scritture, e a prova
            // finita l'API le rifiuta. Non ha senso aprire la pagina.
            path: "variazioni",
            name: "variazioni-prezzo",
            component: () => import("../views/VariazioniPrezzoPage.vue"),
            meta: {
              writes: true,
              breadcrumb: "Variazioni di prezzo",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: true,
              requiresAuth: true,
              requiresAdmin: true,
            },
          },
          {
            path: ":category",
            name: "listini-category",
            component: ListiniCategoryPage,
            props: true,
            meta: {
              breadcrumb: "Categoria",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: true,
              requiresAuth: true,
              permission: ["listini", "V"],
            },
            children: [
              {
                path: "nuovo",
                name: "listini-add-item",
                component: CreateItemPage,
                meta: {
                  writes: true, // [sc-48497] chiusa a prova finita
                  breadcrumb: "Nuovo articolo",
                  hideAccountLink: false,
                  hideUrlIndex: false,
                  showLogo: false,
                  requiresAuth: true,
                  permission: ["listini", "C"],
                },
              },
              {
                path: "modifica/:itemId",
                name: "listini-edit-item",
                component: EditItemPage,
                props: true,
                meta: {
                  writes: true, // [sc-48497] chiusa a prova finita
                  breadcrumb: "Modifica articolo",
                  hideAccountLink: false,
                  hideUrlIndex: false,
                  showLogo: false,
                  requiresAuth: true,
                  permission: ["listini", "M"],
                },
              },
            ],
          },
        ],
      },
      {
        path: "magazzino",
        name: "magazzino",
        component: MagazzinoPage,
        meta: {
          breadcrumb: "Magazzino",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          permission: ["magazzino", "V"],
        },
      },
      {
        path: "preventivi",
        name: "quotes",
        component: QuotePage,
        meta: {
          breadcrumb: "Preventivi",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          permission: ["preventivi", "V"],
          requiresAdmin: true,
        },
        children: [
          {
            path: "nuovo",
            name: "add-quote",
            component: CreateQuotePage,
            meta: {
              writes: true, // [sc-48497] chiusa a prova finita
              breadcrumb: "Nuovo preventivo",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              permission: ["preventivi", "C"],
              requiresAdmin: true,
            },
          },
          {
            path: "dettaglio/:quoteId",
            name: "view-quote",
            component: EditQuotePage,
            props: (route) => ({
              quoteId: route.params.quoteId,
              readOnly: true,
            }),
            meta: {
              breadcrumb: "Dettaglio preventivo",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              permission: ["preventivi", "V"],
            },
          },
          {
            path: "modifica/:quoteId",
            name: "edit-quote",
            component: EditQuotePage,
            props: true,
            meta: {
              writes: true, // [sc-48497] chiusa a prova finita
              breadcrumb: "Modifica preventivo",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              permission: ["preventivi", "M"],
              requiresAdmin: true,
            },
          },
        ],
      },
      {
        path: "carico-merce",
        name: "carico-merce",
        component: OrderPage,
        meta: {
          writes: true, // [sc-48497] chiusa a prova finita
          breadcrumb: "Carico Merce",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          permission: ["magazzino", "V"],
        },
      },
      {
        // [sc-49222] Area Fatturazione: elenco delle fatture elettroniche.
        // Il dettaglio è la figlia `fatturazione-dettaglio`.
        path: "fatturazione",
        name: "fatturazione",
        component: FatturazionePage,
        meta: {
          breadcrumb: "Fatturazione",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          permission: ["fatture", "V"],
          requiresAdmin: true,
        },
        children: [
          {
            path: "dettaglio/:invoiceId",
            name: "fatturazione-dettaglio",
            component: FatturaDettaglioPage,
            props: true,
            meta: {
              breadcrumb: "Dettaglio fattura",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              permission: ["fatture", "V"],
            },
          },
        ],
      },
      {
        path: "ordini",
        name: "ordini",
        component: OrdiniPage,
        meta: {
          breadcrumb: "Ordini",
          hideAccountLink: false,
          hideUrlIndex: false,
          showLogo: true,
          requiresAuth: true,
          permission: ["ordini", "V"],
          requiresAdmin: true,
        },
        children: [
          {
            path: "dettaglio/:quoteId",
            name: "view-order",
            component: EditQuotePage,
            props: (route) => ({
              quoteId: route.params.quoteId,
              readOnly: true,
            }),
            meta: {
              breadcrumb: "Dettaglio ordine",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              permission: ["ordini", "V"],
            },
          },
          {
            path: "modifica/:quoteId",
            name: "edit-order",
            component: EditQuotePage,
            props: true,
            meta: {
              writes: true, // [sc-48497] chiusa a prova finita
              breadcrumb: "Modifica ordine",
              hideAccountLink: false,
              hideUrlIndex: false,
              showLogo: false,
              requiresAuth: true,
              permission: ["ordini", "M"],
              requiresAdmin: true,
            },
          },
        ],
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.VUE_APP_BASE_URL),
  routes,
});

// ---------------------------------------------------------------------------
// Navigation guard globale
// ---------------------------------------------------------------------------
router.beforeEach(async (to) => {
  // (0) Sezione Listini. In modalità demo è chiusa (il CSS ne nasconde il
  // punto di accesso, questa guardia impedisce di arrivarci incollando l'URL);
  // fuori dalla demo esiste una sola scheda e gli URL verso le vecchie
  // categorie merceologiche vengono rifiutati. La decisione è in
  // `listiniRedirectFor`, che è pura e coperta da test.
  const listiniRedirect = listiniRedirectFor(to);
  if (listiniRedirect) {
    return { name: listiniRedirect, replace: true };
  }

  // (A) Cleanup: evita catene /login?redirect=/login?redirect=...
  if (
    to.name === "login" &&
    typeof to.query.redirect === "string" &&
    to.query.redirect.startsWith("/login")
  ) {
    const rest = { ...to.query };
    delete rest.redirect; // rimuove il parametro senza creare variabili inutilizzate
    return { name: "login", query: rest, replace: true };
  }

  const needsGuest = to.matched.some((r) => r.meta?.requiresGuest);

  // [sc-48497] Accesso e registrazione sono pagine di piattaforma: nessun
  // cliente le possiede. Lo store può arrivare qui col marchio dell'azienda da
  // cui si è appena usciti — e i colori sono variabili CSS globali, che
  // restano finché qualcuno non le riscrive. Si azzera prima di entrare, così
  // non c'è nemmeno il lampeggio in attesa della risposta dell'API.
  if (to.matched.some((r) => r.meta?.platformBrand)) {
    store.dispatch("resetCompanyBranding");
  }
  const needsAuth = to.matched.some((r) => r.meta?.requiresAuth);

  // Rotte "neutre"
  if (!needsGuest && !needsAuth) return true;

  // (B) Ping al BE: alla prima navigazione, o dopo login (cachedAuth=true ma user non ancora caricato)
  if (!fetchedOnce || (cachedAuth && !cachedUser)) {
    try {
      const { data } = await axios.get(WHO_AM_I, { withCredentials: true });
      cachedAuth = true;
      cachedUser = data;
      store.commit("SET_CURRENT_USER", data);
      // [sc-46314] Lo stato del tutorial viaggia col profilo: nessuna
      // chiamata in più a ogni accesso, ed è pronto prima che la home si
      // monti — è così che il modale non lampeggia.
      store.commit("SET_ONBOARDING", data.onboarding ?? null);
      // [sc-48497 / task 48522] Attesa, non lanciata e dimenticata: da qui
      // arriva anche `configured`, e il cancello qui sotto lo legge subito
      // dopo. Prima veniva dalla rotta pubblica del marchio, che con più
      // aziende sulla stessa installazione non sa di chi stia parlando e
      // rispondeva `configured: false` a chi la configurazione l'aveva
      // appena chiusa, lasciandolo chiuso fuori dal gestionale.
      await store.dispatch("fetchCompanyProfile");
    } catch {
      cachedAuth = false;
      cachedUser = null;
      store.commit("CLEAR_CURRENT_USER");
    } finally {
      fetchedOnce = true;
    }
  }

  // (B-bis) Configurazione iniziale. Finché non è chiusa, il cliente non entra
  // nel gestionale: troverebbe un'istanza senza marchio e senza unità di
  // vendita, cioè un gestionale in cui non può nemmeno creare un articolo.
  // La decisione sta in `setupRedirectFor`, che è pura e coperta da test.
  const setupRedirect = setupRedirectFor({
    routeName: to.name,
    authenticated: cachedAuth,
    user: cachedUser,
    configured: Boolean(store.getters["company/branding"]?.configured),
  });
  if (setupRedirect) {
    return { name: setupRedirect, replace: true };
  }

  // (B-ter) Console di piattaforma [sc-48497 / task 48534]. Le due parti non si
  // incrociano: l'amministratore di crmstart nel gestionale troverebbe solo
  // errori, e un cliente nella console troverebbe i numeri degli altri.
  const platformRedirect = platformRedirectFor({
    authenticated: cachedAuth,
    user: cachedUser,
    isPlatformRoute: to.matched.some((r) => r.meta?.platformOnly),
  });
  if (platformRedirect) {
    return { name: platformRedirect, replace: true };
  }

  // (C) Regole di redirect
  if (needsAuth && !cachedAuth) {
    if (to.name === "login") return true;
    return { name: "login", query: { redirect: to.fullPath }, replace: true };
  }

  if (needsGuest && cachedAuth) {
    return { name: "home", replace: true };
  }

  // [sc-48497 / task 48527] A prova finita il gestionale si legge ma non si
  // scrive. Il rifiuto vero lo dà il server; qui si evita di far compilare un
  // modulo che poi non si salva, che è il modo peggiore di dare la notizia.
  if (
    to.matched.some((r) => r.meta?.writes) &&
    store.getters["company/readOnly"]
  ) {
    return { name: "home", replace: true };
  }

  // [sc-48351 / task 48786] Con i ruoli (PLUS) una pagina che chiede un
  // permesso lo chiede al ruolo, non a isAdmin: il commerciale apre i clienti
  // senza essere amministratore. Restano dell'amministratore le pagine senza
  // permesso (marchio, log, dati, variazioni, ruoli). In START non cambia
  // niente: le pagine sono degli amministratori come prima.
  const ruoli = Boolean(store.getters["company/features"]?.roles);
  const needsAdmin = to.matched.some(
    (r) => r.meta?.requiresAdmin && !(ruoli && r.meta?.permission)
  );
  if (needsAdmin && !cachedUser?.isAdmin) {
    return { name: "home", replace: true };
  }

  if (ruoli && cachedAuth) {
    // Chi apre l'indirizzo a mano finisce sulla prima pagina che può vedere.
    // Il permesso più vicino alla foglia vince: `preventivi/nuovo` chiede C,
    // non solo V.
    const can = store.getters["auth/can"];
    const permesso = [...to.matched].reverse().find((r) => r.meta?.permission)
      ?.meta.permission;
    if (permesso && !can(...permesso)) {
      return { path: primaPaginaVisibile(can), replace: true };
    }
    if (to.name === "home" && !can("dashboard", "V")) {
      const dove = primaPaginaVisibile(can);
      if (dove !== "/home") return { path: dove, replace: true };
    }
  }

  return true;
});

// ---------------------------------------------------------------------------
// [sc-48497 / task 48527] Sola lettura: le scritture si fermano qui.
//
// La guardia di rotta chiude le schermate che scrivono, ma le azioni non sono
// tutte schermate: «archivia cliente» è un pulsante sulla stessa pagina
// dell'elenco, e così il cambio di stato di un preventivo o la generazione di
// un DDT. Elencarle una per una vorrebbe dire dimenticarne una, e dimenticarla
// significa far partire una richiesta che il server rifiuta con un errore
// rosso: la notizia peggiore, nel modo peggiore.
//
// Qui invece si guarda il metodo, che è la stessa regola dell'API. Le due
// eccezioni sono le stesse dichiarate in `trial.middleware.js`: uscire si può
// sempre, e i propri dati si correggono anche mentre si decide se attivare.
// ---------------------------------------------------------------------------

const METODI_CHE_SCRIVONO = new Set(["post", "put", "patch", "delete"]);
const SCRIVIBILI_A_PROVA_FINITA = ["/auth/", "/company-profile"];
// Eccezione dell'eccezione: aggiungere un operatore occupa un posto del piano.
// È una scrittura del cliente travestita da rotta di autenticazione, e lato API
// si ferma allo stesso modo.
const BLOCCATE_ANCHE_SOTTO_AUTH = ["/auth/create-admin"];

axios.interceptors.request.use((config) => {
  const metodo = (config.method || "get").toLowerCase();
  if (!METODI_CHE_SCRIVONO.has(metodo)) return config;
  if (!store.getters["company/readOnly"]) return config;

  const url = config.url || "";
  const bloccata = BLOCCATE_ANCHE_SOTTO_AUTH.some((p) => url.startsWith(p));
  if (!bloccata && SCRIVIBILI_A_PROVA_FINITA.some((p) => url.startsWith(p))) {
    return config;
  }

  const messaggio =
    "La prova è finita: puoi consultare i tuoi dati, ma non modificarli.";
  useToast().warning(messaggio);

  // Forma di un errore axios, così chi chiama non deve distinguere questo
  // rifiuto da quello del server: stesso codice, stessa struttura.
  const err = new Error(messaggio);
  err.config = config;
  err.response = {
    status: 403,
    data: { code: "TRIAL_EXPIRED", message: messaggio },
  };
  return Promise.reject(err);
});

// ---------------------------------------------------------------------------
// Interceptor Axios: se il token scade (401) → chiedi di rifare login
// ---------------------------------------------------------------------------

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const current = router.currentRoute.value;
    const url = err?.config?.url || "";

    const isOnLogin = current?.name === "login";
    const isAuthEndpoint = url.startsWith("/auth/");

    if (status === 401) {
      setAuth(false);

      // Evita loop: se sei su /login o l’endpoint è pubblico, non reindirizzare
      if (!isOnLogin && !isAuthEndpoint) {
        router.replace({
          name: "login",
          query: { redirect: current.fullPath },
        });
      }
    } else if (status === 403) {
      // [sc-48497 / task 48527] Una prova finita è un 403 come gli altri, ma
      // non è un errore di permessi: il cliente è al suo posto, è il piano che
      // è scaduto. Si aggiorna lo stato — così il banner compare da sé — e si
      // lascia la pagina dov'è, invece di buttar via quello che stava
      // scrivendo.
      if (err?.response?.data?.code === "RETENTION_ENDED" && !isAuthEndpoint) {
        // [sc-48583 / task 48588] Passati i novanta giorni la sessione non
        // vale piu' niente: il token dura tre mesi, e senza questo chi era
        // già dentro resterebbe a guardare un archivio che non c'è più. Si
        // esce e si dice perché, invece di lasciare schermate vuote.
        setAuth(false);
        store.dispatch("resetCompanyBranding");
        // Il cookie è HttpOnly: da qui non si cancella, lo può togliere solo
        // il server. Senza questa chiamata resterebbe valido per tre mesi, e
        // a ogni caricamento la guardia rifarebbe il giro - sessione buona,
        // profilo rifiutato - rimettendo l'avviso su una pagina di accesso
        // che il cliente non ha chiesto.
        axios.post("/auth/logout").catch(() => {});
        if (!isOnLogin) {
          router.replace({
            name: "login",
            query: { motivo: "retention" },
          });
        }
      } else if (err?.response?.data?.code === "TRIAL_EXPIRED") {
        store.dispatch("fetchCompanyProfile");
      } else if (err?.response?.data?.code === "PERMISSION_DENIED") {
        // [sc-48351 / task 48786] Il ruolo non lo permette: si resta dove si è
        // e si dice perché. Succede se un ruolo cambia mentre si lavora.
        useToast().warning("Il tuo ruolo non permette questa operazione.");
      } else if (!isOnLogin) {
        router.replace({ name: "home" });
      }
    }
    return Promise.reject(err);
  }
);

export default router;
